import type { ReactNode } from "react";
import { Spin } from "antd";
import { SegmentedControl } from "./SegmentedControl";

/** Опция на настройка: стойност, етикет в превключвателя и подсказка под него. */
export interface SettingOption<V extends string | boolean> {
  value: V;
  label: string;
  hint: string;
}

interface SettingRowProps<V extends string | boolean> {
  name: string;
  description: string;
  options: SettingOption<V>[];
  value: V;
  saving: boolean;
  onChange: (value: V) => void;
  /** Илюстрация под подсказката (напр. лентата над каталога). */
  illustration?: ReactNode;
}

/**
 * Един ред с настройка в секция: вляво името и описанието; вдясно
 * сегментираният превключвател и подсказката за избраното.
 * На тесен контейнер двете колони се подреждат една под друга.
 */
export function SettingRow<V extends string | boolean>({
  name,
  description,
  options,
  value,
  saving,
  onChange,
  illustration,
}: SettingRowProps<V>) {
  const selected = options.find((option) => option.value === value);
  const change = (next: V) => {
    if (saving || next === value) return;
    onChange(next);
  };

  return (
    <div className="grid gap-3 px-5 py-4 @2xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] @2xl:gap-8">
      <div className="min-w-0">
        <div className="text-[13.5px] font-bold tracking-[-0.2px] text-ink">
          {name}
        </div>
        <div className="mt-1 text-[12.5px] leading-[1.45] text-muted">
          {description}
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <SegmentedControl
            options={options}
            value={value}
            disabled={saving}
            onChange={change}
          />
          {saving && <Spin size="small" />}
        </div>
        {selected && (
          <div className="mt-2.5 text-[12.5px] leading-[1.45] text-soft">
            {selected.hint}
          </div>
        )}
        {illustration}
      </div>
    </div>
  );
}
